import { useState, useEffect, useCallback } from 'react';
import {
  listBroll,
  createBroll,
  deleteBroll,
  generateBrollIdeas,
  generateImages,
  persistMedia,
  downloadMedia,
  type ContentProject,
  type ContentScene,
  type ContentBroll,
} from '@/lib/contentStudio';

interface Props {
  project: ContentProject;
  scenes: ContentScene[];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * B-roll IA (spec 35) : l'IA propose des plans d'illustration à partir de la
 * narration, puis génère de vraies images IA pour chacun. L'utilisateur peut
 * les garder, les télécharger ou les supprimer.
 */
export default function BrollPanel({ project, scenes }: Props) {
  const [items, setItems] = useState<ContentBroll[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scopeSceneId, setScopeSceneId] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listBroll(project.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const scene = scopeSceneId ? scenes.find((s) => String(s.id) === scopeSceneId) : null;
      const narration = scene ? (scene.voiceover || scene.script || '') : (project.script || project.prompt || '');
      if (!narration.trim()) {
        throw new Error('Aucune narration à illustrer. Choisissez une scène ou générez d\u2019abord le script.');
      }
      const ideas = await generateBrollIdeas({ narration, language: project.language, style: project.style || undefined });
      if (ideas.length === 0) throw new Error("L'IA n'a proposé aucun plan B-roll.");
      const targetSceneId = scene ? scene.id : null;
      const created: ContentBroll[] = [];
      for (const idea of ideas.slice(0, 6)) {
        // eslint-disable-next-line no-await-in-loop
        const urls = await generateImages({ prompt: idea.prompt, n: 1, aspectRatio: project.aspect_ratio, resolution: '720p' });
        if (urls[0]) {
          // Re-hébergement pour un lien permanent.
          let url = urls[0];
          // eslint-disable-next-line no-await-in-loop
          try { url = await persistMedia(urls[0], `broll-${Date.now()}.jpg`, 'content-studio/broll', 'image/jpeg'); } catch { url = urls[0]; }
          // eslint-disable-next-line no-await-in-loop
          const row = await createBroll(project.id, {
            scene_id: targetSceneId,
            label: idea.label,
            prompt: idea.prompt,
            url,
            provider: 'xAI Grok Imagine',
          });
          if (row) created.push(row);
        }
      }
      setItems((prev) => [...created, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la génération B-roll');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteBroll(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const sceneLabel = (sceneId: number | null): string => {
    if (!sceneId) return 'Projet';
    const s = scenes.find((x) => x.id === sceneId);
    return s ? `Scène ${pad(s.number)}` : 'Scène';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-movie-line text-accent-500"></i>B-roll IA
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            L&apos;IA propose des plans d&apos;illustration à partir de votre narration, puis génère les images correspondantes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={scopeSceneId}
              onChange={(e) => setScopeSceneId(e.target.value)}
              className="appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-xs text-foreground-700 focus:outline-none focus:border-accent-300 cursor-pointer"
            >
              <option value="">Tout le projet</option>
              {scenes.map((s) => <option key={s.id} value={s.id}>Scène {pad(s.number)}</option>)}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 text-xs pointer-events-none"></i>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
            {busy ? 'Génération…' : 'Générer le B-roll'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-movie-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucun plan B-roll</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">Générez des plans d&apos;illustration pour enrichir votre vidéo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((b) => (
            <div key={b.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
              <div className="relative aspect-video bg-background-100 overflow-hidden">
                {b.url ? <img src={b.url} alt={b.label || 'B-roll'} className="w-full h-full object-cover" /> : <i className="ri-image-line text-2xl text-foreground-300"></i>}
                <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-[10px] font-medium">
                  {sceneLabel(b.scene_id)}
                </span>
              </div>
              <div className="p-2.5 flex-1 flex flex-col">
                <p className="text-xs font-semibold text-foreground-800 truncate" title={b.label || ''}>{b.label || 'Plan B-roll'}</p>
                <p className="text-[10px] text-foreground-400 line-clamp-2 mt-0.5">{b.prompt}</p>
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-background-200/70">
                  {b.url && (
                    <button
                      type="button"
                      onClick={() => downloadMedia(b.url as string, `broll-${b.id}.jpg`)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md border border-background-200/70 text-[11px] font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-download-2-line"></i>Télécharger
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(b.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Supprimer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}